import GameConfig from "../core/data/GameConfig";
import ResizableScene from "../core/ResizableScene";
import { HelpPopup } from "../game/HelpPopup";
import { HiddenObject } from "../game/HiddenObject";
import HiddenObjectBar from "../game/HiddenObjectBar";
import { HiddenObjectGame, HiddenObjectGameData } from "../game/HiddenObjectGame";
import { HiddenObjectLevel } from "../game/HiddenObjectLevel";
import GameColors from "../GameColors";
import PlayerData from "../PlayerData";
import { ConfirmQuitPopup } from "../ui/ConfirmQuitPopup";
import { PausePopup } from "../ui/PausePopup";
import { RecapPopup } from "../ui/RecapPopup";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import TimeFormat from "../utils/TimeFormat";

export default class GameScene extends ResizableScene {
	_resize: () => void;

	hiddenObjGame: HiddenObjectGame;
	hiddenObjBar: HiddenObjectBar;

	gameContainer: Phaser.GameObjects.Container;
	uiContainer: Phaser.GameObjects.Container;

	headerBackground: Phaser.GameObjects.Image;

	timerBackground: Phaser.GameObjects.Image;
	timerText: Phaser.GameObjects.Text;
	scoreBackground: Phaser.GameObjects.Image;
	scoreText: Phaser.GameObjects.Text;

	inTutorial = false;

	levelIndex: number;

	init(data: { levelIndex: number }): void {
		this.levelIndex = data.levelIndex;
		super.init();
	}

	create(): void {
		super.create();
		this.music.play("gameMusic");
		const gameData = GameConfig.get("levels") as HiddenObjectGameData[];
		const levelData = gameData[this.levelIndex];

		const widescreenBorder0 = this.add.image(0, 0, "widescreenBorder");
		widescreenBorder0.setOrigin(0.5, 0.5);
		this.add.existing(widescreenBorder0);

		this.gameContainer = this.add.container(0, 0);
		this.uiContainer = this.add.container(0, 0);
		this.gameContainer.setSize(1920, 1080);

		// TODO: Game data should point to level from level select
		this.hiddenObjGame = new HiddenObjectGame(this, levelData);
		this.hiddenObjGame.events.once(HiddenObjectGame.ON_LEVEL_COMPLETE, this.onLevelComplete, this);

		const pauseButton = this.add.button(0, 0, "pauseButton", null, this.onPausePressed, this, true);
		this.uiContainer.add(pauseButton);

		this.timerBackground = this.add.image(0, 0, "timerBackground");
		this.uiContainer.add(this.timerBackground);
		const uiStyle = {
			// color: this.levelIndex < 4 ? GameColors.BLACK : GameColors.WHITE,
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratBold,
			fontSize: "55px",
			align: "center",
		};
		this.timerText = this.add.text(0, 0, ":0", uiStyle);
		this.timerText.setOrigin(0.5, 0.5);
		this.uiContainer.add(this.timerText);

		this.scoreBackground = this.add.image(0, 0, "scoreBackground");
		this.uiContainer.add(this.scoreBackground);
		this.scoreText = this.add.text(0, 0, "0", uiStyle);
		this.scoreText.setOrigin(0.5, 0.5);
		this.uiContainer.add(this.scoreText);

		this.hiddenObjBar = new HiddenObjectBar(this, this.viewport.width);
		this.uiContainer.add(this.hiddenObjBar);

		const widescreenBorder1 = this.add.image(0, 0, "widescreenBorder");
		const widescreenBorder2 = this.add.image(0, 0, "widescreenBorder");
		widescreenBorder1.setOrigin(0.5, 0.5);
		widescreenBorder2.setOrigin(0.5, 0.5);

		widescreenBorder1.angle += 90;
		widescreenBorder2.angle -= 90;

		this.add.existing(widescreenBorder1);
		this.add.existing(widescreenBorder2);

		this._resize = (): void => {
			this.gameContainer.scale = 1;

			this.uiContainer.setSize(this.viewport.width, this.viewport.height);
			this.alignIn.Center(this.gameContainer, this.viewport);

			if (this.gameContainer.width > this.viewport.width) {
				this.gameContainer.scale = this.viewport.width / this.gameContainer.width;
				this.hiddenObjBar.scale = this.gameContainer.scale;
				this.alignIn.TopRight(pauseButton, this.viewport, -30, -30);
				this.alignIn.TopLeft(this.scoreBackground, this.viewport, -10, -20);
			} else {
				this.alignIn.TopRight(pauseButton, this.gameContainer, -30, -30);
				this.alignIn.TopLeft(this.scoreBackground, this.gameContainer, -10, -20);
			}

			// ui
			this.alignIn.TopCenter(this.timerBackground, this.viewport, -20, -20);
			this.alignIn.Center(this.timerText, this.timerBackground);
			this.alignIn.Center(this.scoreText, this.scoreBackground, 20);
			this.alignIn.BottomCenter(this.hiddenObjBar, this.gameContainer);

			this.hiddenObjGame.resizeBG();

			const bakcupBGScale = this.viewport.width / widescreenBorder1.width;
			widescreenBorder0.scale = bakcupBGScale;

			const borderScale = this.viewport.height / widescreenBorder1.width;
			widescreenBorder1.scale = borderScale;
			widescreenBorder2.scale = borderScale;

			this.alignIn.LeftCenter(widescreenBorder1, this.gameContainer, widescreenBorder1.height + 810);
			this.alignIn.RightCenter(widescreenBorder2, this.gameContainer, widescreenBorder2.height + 810);

			this.alignIn.Center(widescreenBorder0, this.viewport);
		};
		this.resize();

		ARIA.addTextGroup({
			container: this.scoreBackground,
			textField: this.scoreText,
			setText: (text: string) => `Current Score: ${text}`,
			focus: true,
		});
		ARIA.addTextGroup({
			container: this.timerBackground,
			textField: this.timerText,
			setText: (text: string) => {
				const currentTime = this.hiddenObjGame.levelStartTimer;
				const minutes = Math.floor(currentTime / 60);
				const seconds = Math.floor(currentTime % 60);
				text = `${seconds.toFixed()} seconds`;
				if (minutes > 0) text = `${minutes.toFixed()} minutes and ${text}`;
				return `Time Elapsed: ${text}`;
			},
		});
		pauseButton.setARIA("Pause");

		this.hiddenObjGame.loadNextLevel(levelData.id);
		this.startGame();

		this.hiddenObjGame.currentLevel.events.addListener(HiddenObjectLevel.ON_OBJECT_COLLECTED, this.updateObjectsFound, this);

		this.hiddenObjGame.bgImages[0].setInteractive();
		this.hiddenObjGame.bgImages[0].on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => {
			this.soundManager.play("objectNotFoundSFX");
		});

		// End game at current time with gold
		// this.input.keyboard.on("keydown-K", (): void => {
		// 	this.pauseGame();
		// 	this.hiddenObjGame.currentLevel.score = 999999;
		// 	const popup = new RecapPopup(this, this.hiddenObjGame.data, this.hiddenObjGame.levelStartTimer);
		// 	this.uiContainer.add(popup);
		// });

		// // End game with silver
		// this.input.keyboard.on("keydown-L", (): void => {
		// 	this.pauseGame();
		// 	this.hiddenObjGame.currentLevel.score = this.hiddenObjGame.data.objects * 65;
		// 	const popup = new RecapPopup(this, this.hiddenObjGame.data, 10000);
		// 	this.uiContainer.add(popup);
		// });

		// // End game with bronze
		// this.input.keyboard.on("keydown-B", (): void => {
		// 	this.pauseGame();
		// 	this.hiddenObjGame.currentLevel.score = this.hiddenObjGame.data.objects * 10;
		// 	const popup = new RecapPopup(this, this.hiddenObjGame.data, 10000);
		// 	this.uiContainer.add(popup);
		// });

		if (!PlayerData.Instance.saveData.seenTutorial) this.createHelpPopup(true);
	}

	shutdown(): void {
		this.hiddenObjGame.bgImages[0].off(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN);
		super.shutdown();
	}

	startGame(): void {
		this.hiddenObjGame.isPaused = false;
	}

	update(time: number, delta: number): void {
		super.update(time, delta);

		if (!this.hiddenObjGame) return;
		this.hiddenObjGame.update(time, delta);
		this.timerText.setText(TimeFormat.format(this.hiddenObjGame.levelStartTimer));
		this.alignIn.Center(this.timerText, this.timerBackground, 20);
	}

	onLevelComplete(): void {
		this.pauseGame();
		const popup = new RecapPopup(this, this.hiddenObjGame.data, this.hiddenObjGame.levelStartTimer);
		this.uiContainer.add(popup);
		// this.scene.start("PreloaderScene", { destinationScene: "SplashScene" });
		// this.scene.stop(this.scene.key);
	}

	pauseGame(): void {
		this.hiddenObjGame.isPaused = true;
		this.hiddenObjGame.currentLevel.pause();
		this.pausePhaserSystems();
	}

	resumeGame(): void {
		if (!this.inTutorial) this.hiddenObjGame.isPaused = false;
		this.hiddenObjGame.currentLevel.resume();
		this.resumePhaserSystems();
	}

	pausePhaserSystems(): void {
		this.physics.world.pause();
		this.time.paused = true;
		this.tweens.pauseAll();
		this.anims.pauseAll();
	}

	resumePhaserSystems(): void {
		this.physics.world.resume();
		this.time.paused = false;
		this.tweens.resumeAll();
		this.anims.resumeAll();
	}

	onPausePressed(fadeIn = true): void {
		this.pauseGame();
		const pop = new PausePopup(
			this,
			() => {
				this.resumeGame();
				pop.destroy();
				ARIA.showLayer(undefined, true);
			},
			this,
			() => {
				pop.destroy();
				this.createHelpPopup();
			},
			this,
			() => {
				pop.destroy();
				this.createQuitPopup();
			},
			this,
			fadeIn
		);
		this.uiContainer.add(pop);
	}

	createHelpPopup(firstTime = false): void {
		if (!firstTime) {
			const popup = new HelpPopup(this, () => this.onPausePressed(false), firstTime, false);
			this.uiContainer.add(popup);
		} else {
			this.pauseGame();
			const popup = new HelpPopup(this, () => this.showTutorial(), firstTime, true);
			this.uiContainer.add(popup);
		}
	}

	createQuitPopup(): void {
		const quitPopup = new ConfirmQuitPopup(
			this,
			null,
			this,
			() => {
				quitPopup.destroy();
				this.onPausePressed(false);
			},
			this
		);
		this.uiContainer.add(quitPopup);
	}

	updateObjectsFound(): void {
		this.soundManager.play("objectFoundSFX");
		const level = this.hiddenObjGame.currentLevel;
		this.scoreText.setText(level.score.toString());
		this.alignIn.Center(this.scoreText, this.scoreBackground, 20);
	}

	showTutorial(): void {
		ARIA.showLayer(undefined, true);

		this.inTutorial = true;
		this.hiddenObjGame.isPaused = true;
		const hiddenObj = this.hiddenObjGame.currentLevel.hiddenObjects[0];
		const hiddenObjImage = hiddenObj.image;
		const tutObj = this.add.image(0, 0, "tutHand").setScale(0);
		this.uiContainer.add(tutObj);

		const timeline = this.tweens.createTimeline();

		const outro = (): void => {
			this.tweens.add({
				targets: tutObj,
				scale: 0,
				duration: 350,
				ease: Phaser.Math.Easing.Quadratic.Out,
				onComplete: () => tutObj.destroy(),
			});
		};

		hiddenObj.events.once(
			HiddenObject.ON_PRESSED,
			() => {
				this.inTutorial = false;
				this.hiddenObjGame.isPaused = false;
				this.tweens.killTweensOf(tutObj);
				PlayerData.Instance.saveData.seenTutorial = true;
				PlayerData.Instance.save();
				outro();
			},
			this
		);

		const thumbMat = this.hiddenObjBar.slots[0].getWorldTransformMatrix();
		const thumbX = thumbMat.getX(0, 0) + tutObj.width * 2;
		const thumbY = thumbMat.getY(0, 0) + tutObj.height * 0.4;

		tutObj.setPosition(thumbX, thumbY);

		this.alignIn.LeftCenter(tutObj, this.hiddenObjBar, -400, 50);
		timeline.add({
			targets: tutObj,
			scale: 1,
			delay: 500,
			duration: 350,
			hold: 1000,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		timeline.add({
			targets: tutObj,
			x: this.viewport.width * 0.7,
			y: this.viewport.height / 2,
			duration: 1000,
			hold: 500,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		timeline.add({
			targets: tutObj,
			x: this.viewport.width * 0.2,
			y: this.viewport.height * 0.3,
			duration: 1000,
			hold: 500,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		const hiddenObjMat = hiddenObjImage.getWorldTransformMatrix();
		const hiddenObjx = hiddenObjMat.getX(0, 0) + tutObj.width / 2 - 20;
		const hiddenObjy = hiddenObjMat.getY(0, 0) + tutObj.height * 0.4;

		timeline.add({
			targets: tutObj,
			x: hiddenObjx,
			y: hiddenObjy,
			duration: 1000,
			hold: 500,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		timeline.add({
			targets: tutObj,
			scale: 1.1,
			duration: 350,
			yoyo: true,
			repeat: -1,
			repeatDelay: 750,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});
		timeline.play();
	}

	resize(): void {
		super.resize();
		if (this._resize) this._resize();
	}
}
